import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AutocompleteRenderInputParams } from '@hopskipdrive/react-components';

import { RiderOption } from '../../../hooks/useRiderGroupedOptions';
import { RiderOnRide } from '../../../types/rides';

import { RiderDropdownSearch } from './RiderDropdownSearch';
import RiderInfoModal from './RiderInfoModal';

interface SelectRiderProps {
  waypointIndex: number;
  isAnchorWaypoint: boolean;
  riderOptions: RiderOption[];
  isFetching: boolean;
  ridersOnRide: RiderOnRide[];
  organizerName: string;
  renderInput: (params: AutocompleteRenderInputParams) => JSX.Element;
  onAdd: (riders: RiderOption[]) => void;
  onRemove: (riderId: string) => void;
}

export const RiderDropdown: FC<SelectRiderProps> = ({
  waypointIndex,
  isAnchorWaypoint,
  riderOptions,
  isFetching,
  ridersOnRide,
  organizerName,
  renderInput,
  onAdd,
  onRemove,
}) => {
  const { setValue, getValues } = useFormContext();
  const [selectedRiders, setSelectedRiders] = useState<RiderOption[]>([]);
  const [selectedRiderSlug, setSelectedRiderSlug] = useState<string | undefined>();
  const passengerIdsWaypointFieldKey = `waypointsAttributes.${waypointIndex}.passengerIdsStrings`;
  const currentRiderIds = getValues(passengerIdsWaypointFieldKey);

  const handleSelectedRiders = (_, value) => {
    setSelectedRiders(value);
    onAdd(value);
  };

  const handleRemoveSelectedRiders = (optionToDelete) => {
    setSelectedRiders(selectedRiders.filter((rider) => rider.value !== optionToDelete.value));
    onRemove(optionToDelete.value);
  };

  useEffect(() => {
    if (!isAnchorWaypoint && currentRiderIds?.length) {
      setSelectedRiders(
        ridersOnRide
          .filter((rider) => currentRiderIds.indexOf(rider.id.toString()) !== -1)
          .map((rider) => ({
            value: rider.id,
            slug: rider.slug,
            label: rider.firstName + ' ' + rider.lastName,
            group: 'Riders on Ride',
            profilePictureUrl: rider.profilePictureUrl,
            organizerName: rider,
          }))
      );
    }
  }, [currentRiderIds, ridersOnRide, isAnchorWaypoint]);

  useEffect(() => {
    if (isAnchorWaypoint) {
      setSelectedRiders(
        ridersOnRide.map((rider) => ({
          value: rider.id,
          slug: rider.slug,
          label: rider.firstName + ' ' + rider.lastName,
          group: 'Riders on Ride',
          profilePictureUrl: rider.profilePictureUrl,
        }))
      );
      // register anchor selected riders
      setValue(
        passengerIdsWaypointFieldKey,
        ridersOnRide.map((rider) => rider.id.toString())
      );
    }
  }, [isAnchorWaypoint, ridersOnRide]);

  useEffect(() => {
    if (!isAnchorWaypoint && !currentRiderIds?.length) {
      setSelectedRiders([]);
    }
  }, [currentRiderIds, isAnchorWaypoint]);

  return (
    <>
      <RiderDropdownSearch
        riderOptions={riderOptions}
        isFetching={isFetching}
        selectedRiders={selectedRiders}
        handleSelectedRiders={handleSelectedRiders}
        handleRemoveSelectedRiders={handleRemoveSelectedRiders}
        onRiderClick={(riderSlug) => setSelectedRiderSlug(riderSlug)}
        renderInput={renderInput}
      />
      {selectedRiderSlug && (
        <RiderInfoModal
          riderSlug={selectedRiderSlug}
          onClose={() => setSelectedRiderSlug(undefined)}
          organizerName={organizerName}
          profileUrl="https://admin.hopskipdrive.com"
        />
      )}
    </>
  );
};
